import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from '@phosphor-icons/react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

function createNumberedIcon(number, active) {
  const bg = active ? '#3f6212' : '#65a30d';
  const ring = active ? 'box-shadow:0 0 0 4px rgba(132,204,22,0.35),0 2px 6px rgba(0,0,0,0.3)' : 'box-shadow:0 2px 6px rgba(0,0,0,0.3)';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background:${bg};color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid white;${ring}">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
}

// Internal controller — uses Leaflet imperative API to fly the map to
// the currently selected location. Rendered inside <MapContainer>.
function MapController({ target, defaultCenter, defaultZoom }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (target) {
      map.flyTo([target.lat, target.lng], 14, { duration: 0.9 });
    } else {
      map.flyTo(defaultCenter, defaultZoom, { duration: 0.9 });
    }
  }, [target, map, defaultCenter, defaultZoom]);
  return null;
}

export function LocationsMap({
  locations,
  cardTestIdPrefix = 'location-card',
  mapTestId = 'locations-map',
  // Initial zoom — set to 10 (was 11) so every marker including the
  // most outlying one is visible on first paint.
  defaultZoom = 10,
}) {
  const usable = locations.filter(l => l.lat && l.lng);
  const [selectedId, setSelectedId] = useState(null);
  const markerRefs = useRef({});

  if (usable.length === 0) return null;

  const defaultCenter = [
    usable.reduce((s, l) => s + l.lat, 0) / usable.length,
    usable.reduce((s, l) => s + l.lng, 0) / usable.length,
  ];

  const selected = usable.find(l => l.id === selectedId) || null;

  const handleCardClick = (loc) => {
    if (selectedId === loc.id) {
      setSelectedId(null);
      return;
    }
    setSelectedId(loc.id);
    // Open the popup on the marker once flyTo finishes
    setTimeout(() => {
      const m = markerRefs.current[loc.id];
      if (m && m.openPopup) m.openPopup();
    }, 950);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="rounded-2xl overflow-hidden border border-neutral-200 shadow-lg"
        style={{ height: '420px' }}
        data-testid={mapTestId}
      >
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController target={selected} defaultCenter={defaultCenter} defaultZoom={defaultZoom} />
          {usable.map(loc => (
            <Marker
              key={loc.id}
              position={[loc.lat, loc.lng]}
              icon={createNumberedIcon(loc.number, selectedId === loc.id)}
              ref={(r) => { if (r) markerRefs.current[loc.id] = r; }}
              eventHandlers={{ click: () => setSelectedId(loc.id) }}
            >
              <Popup>
                <strong>{loc.name}</strong><br />
                <span style={{ fontSize: '12px', color: '#666' }}>{loc.address}</span>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-4">
        {locations.map((loc, i) => {
          const isActive = selectedId === loc.id;
          const clickable = !!(loc.lat && loc.lng);
          return (
            <motion.div
              key={loc.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : -1}
              onClick={clickable ? () => handleCardClick(loc) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(loc); } } : undefined}
              className={`bg-white rounded-xl border p-4 transition-all ${clickable ? 'cursor-pointer hover:border-lime-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-lime-500' : 'border-neutral-200'} ${isActive ? 'border-lime-500 shadow-md bg-lime-50/40' : 'border-neutral-200'}`}
              data-testid={`${cardTestIdPrefix}-${i}`}
              aria-pressed={isActive}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isActive ? 'bg-lime-700 text-white' : 'bg-lime-600 text-white'}`}>
                  {loc.number}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-neutral-900 text-sm">{loc.name}</h3>
                  <p className="text-neutral-500 text-xs mt-1 leading-relaxed">{loc.address}</p>
                  {clickable && (
                    <p className={`text-[10px] font-semibold mt-2 inline-flex items-center gap-1 ${isActive ? 'text-lime-700' : 'text-lime-600'}`}>
                      <MapPin size={11} weight="fill" />
                      {isActive ? 'Showing on map · click to reset' : 'Click to zoom on map'}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
